import { SidebarTrigger, useSidebar } from 'tailwind-v4/components/sidebar'
import { Separator } from 'tailwind-v4/components/separator'
import { Link, useLocation } from 'react-router'
import {
  findItemChainForPath,
  orgNavPrimary,
  orgNavSecondary,
  resolveHref,
  resolveLabel,
} from './config'
import { VenueSwitcher } from './venue-switcher'
import { MobileNav } from './mobile-nav'
import { useNewNavCtx } from './new-sidebar-shell'
import { SquareEntityPicker } from './square-entity-picker'

export function NewTopBar() {
  const { pathname } = useLocation()
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'
  const navCtx = useNewNavCtx()

  // Derive the serial the same way the nav does — segment 2 behind `venues`.
  const segments = pathname.split('/').filter(Boolean)
  const orgId = segments[0] ?? ''
  const serial =
    segments[1] === 'venues' &&
    typeof segments[2] === 'string' &&
    segments[2].length > 0
      ? segments[2]
      : undefined
  const chain = findItemChainForPath(pathname, { orgId, serial })

  // Discounts, Square Loyalty and Customer Groups are entity-scoped, not
  // venue-scoped: hide the venue picker entirely and surface a Square entity
  // picker in its place. See docs/square-discounts and docs/square-loyalty
  // (neo repo).
  const onDiscounts =
    Boolean(orgId) &&
    (pathname === `/${orgId}/square/discounts` ||
      pathname === `/${orgId}/square/loyalty` ||
      pathname === `/${orgId}/square/customer-groups`)

  // Full Bhuna at ORG scope carries its own multi-select venue filter in the
  // report toolbar, because comparing venues against each other is the point
  // of that view. Two venue controls on one screen invites them to disagree
  // and leaves the reader unsure which one the figures obey, so the global
  // single-venue switcher stands down here.
  //
  // Only at org scope: once a venue IS selected the report scopes to it and
  // drops its own picker, and the global switcher becomes the way back out.
  const onOrgFullBhuna =
    Boolean(orgId) &&
    pathname.startsWith(`/${orgId}/reports/full-bhuna`) &&
    !pathname.includes('/venues/')

  // Synthetic deep-detail crumbs. The nav config is keyed on
  // static URLs, so dynamic-id detail pages (e.g. the email
  // campaign editor) terminate the chain at the parent list.
  // We append a label crumb here so the breadcrumb conveys
  // "you're inside a single campaign" rather than just landing
  // back at "Email Campaigns" — a one-level-deeper readout the
  // operator can scan without needing the URL bar.
  const detailCrumbs: { id: string; label: string }[] = (() => {
    const crumbs: { id: string; label: string }[] = []
    if (
      orgId &&
      pathname.startsWith(`/${orgId}/marketing/email/campaigns/`) &&
      pathname !== `/${orgId}/marketing/email/campaigns`
    ) {
      crumbs.push({ id: 'campaign-detail', label: 'Campaign' })
    }
    return crumbs
  })()

  return (
    <header className="grid h-14 shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2 border-b border-border px-4 bg-[var(--background)] text-[var(--foreground)]">
      <div className="flex items-center gap-2 min-w-0">
        <MobileNav
          primary={orgNavPrimary}
          secondary={orgNavSecondary}
          ctx={navCtx}
        />
        <span className="logo-shimmer inline-block h-6 shrink-0 md:hidden">
          <img src="/favicon.svg" alt="Stampede" className="h-6 w-auto" />
        </span>
        {isCollapsed ? (
          <div className="hidden md:contents">
            <SidebarTrigger className="hover:bg-[var(--foreground)]/5" />
            <Separator
              orientation="vertical"
              className="mx-1 !h-5 self-center"
            />
          </div>
        ) : null}
        {onDiscounts || onOrgFullBhuna ? null : (
          <div className="hidden md:block min-w-0">
            <VenueSwitcher />
          </div>
        )}
        {onDiscounts ? (
          <div className="hidden md:block min-w-0">
            <SquareEntityPicker orgId={orgId} />
          </div>
        ) : null}
      </div>
      <div className="flex items-center justify-center min-w-0">
        {chain.length > 0 || detailCrumbs.length > 0 ? (
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-1.5 text-sm min-w-0"
          >
            {chain.map((item, i) => {
              const isLast = i === chain.length - 1 && detailCrumbs.length === 0
              const href = resolveHref(item, { orgId, serial })
              const label = resolveLabel(item, { orgId, serial })
              return (
                <div
                  key={item.id}
                  className="flex items-center gap-1.5 min-w-0"
                >
                  {i > 0 ? (
                    <span
                      aria-hidden
                      className="text-[var(--muted-foreground)]/60"
                    >
                      /
                    </span>
                  ) : null}
                  {isLast || !href ? (
                    <span className="font-medium truncate">{label}</span>
                  ) : (
                    <Link
                      to={href}
                      className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors truncate"
                    >
                      {label}
                    </Link>
                  )}
                </div>
              )
            })}
            {detailCrumbs.map((crumb, i) => {
              const isLast = i === detailCrumbs.length - 1
              return (
                <div
                  key={crumb.id}
                  className="flex items-center gap-1.5 min-w-0"
                >
                  <span
                    aria-hidden
                    className="text-[var(--muted-foreground)]/60"
                  >
                    /
                  </span>
                  <span
                    className={`truncate ${
                      isLast ? 'font-medium' : 'text-[var(--muted-foreground)]'
                    }`}
                  >
                    {crumb.label}
                  </span>
                </div>
              )
            })}
          </nav>
        ) : null}
      </div>
      <div aria-hidden className="flex items-center justify-end" />
    </header>
  )
}
